import { useGame } from "@/game/GameContext";
import PixelButton from "@/components/PixelButton";
import AshLogo from "@/components/AshLogo";
import { ASH_MEDIA } from "@/game/media";
import {
  setMusicEnabled,
  setSfxEnabled,
  isMusicEnabled,
  isSfxEnabled,
  loadAudioPreference,
  startMenuMusic,
} from "@/game/audioEngine";
import { useEffect } from "react";

export default function StartScreen() {
  const { dispatch } = useGame();

  useEffect(() => {
    loadAudioPreference();
    if (isMusicEnabled()) startMenuMusic();
  }, []);

  const goTo = (screen, autoEnableSound = false) => {
    if (autoEnableSound) {
      if (!isMusicEnabled()) setMusicEnabled(true);
      if (!isSfxEnabled()) setSfxEnabled(true);
      startMenuMusic();
    }
    dispatch({ type: "GOTO", screen });
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <img
        src={ASH_MEDIA.hero_bg}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-60 pixelated"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/30 via-[#0F172A]/40 to-[#0A0F1C]/95" />

      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 pt-3 md:pt-4 pb-8">
        {/* Header: clickable logo + byline */}
        <a
          href="https://www.ash-interactive.com"
          target="_blank"
          rel="noreferrer"
          data-testid="visit-ash-start"
          aria-label="Visit ASH Interactive"
          className="mb-1 flex items-center gap-3 hover:opacity-90 active:opacity-80 transition-opacity cursor-pointer"
        >
          <AshLogo size={44} className="float-y" />
          <div className="ash-byline text-xl md:text-2xl">
            <span className="by">by</span>
            <span className="ash">ASH</span>
            <span className="interactive">interactive</span>
          </div>
        </a>

        {/* Title — sized to land above the mountains */}
        <h1
          data-testid="start-title"
          className="pixel-font text-4xl md:text-7xl text-[#F58634] text-center leading-none mt-1"
          style={{ textShadow: "4px 4px 0 #000, 8px 8px 0 rgba(245,134,52,0.25)" }}
        >
          MARKETING<br />TRAIL
        </h1>

        <p className="vt323 text-lg md:text-2xl text-white/85 mt-4 text-center max-w-xl px-2">
          Survive your first 12 months. Build your business. Make smart moves.
        </p>

        {/* Spacer — pushes buttons toward the bottom to reveal more wagon */}
        <div className="flex-1 min-h-[40px]" />

        {/* Bottom: action buttons (no Visit ASH button — logo is the link) */}
        <div className="flex flex-col gap-3 w-full max-w-[260px]">
          <PixelButton
            data-testid="start-game-button"
            onClick={() => goTo("character", true)}
          >
            START THE TRAIL
          </PixelButton>
          <PixelButton
            variant="secondary"
            data-testid="how-to-play-button"
            onClick={() => goTo("how", true)}
          >
            How To Play
          </PixelButton>
        </div>

        <p className="mt-6 vt323 text-base md:text-lg text-white/60 text-center max-w-md">
          Tip: turn on music and SFX for the full 8-bit experience.
        </p>
      </div>
    </div>
  );
}
